import React from "react";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { GlobalStore } from "./store/global/GlobalStore";
import { Web3 } from "./components/Web3/Web3";
import * as Sentry from "@sentry/react";
import { Trade } from "./pages/Trade/Trade";
import { getEnvString } from "./utils/sentryUtils";
import { Activity } from "./pages/TradeHistory/Activity";
import { NotFound } from "./pages/404/404";
import { ThemeProvider } from "@emotion/react";
import { Wrap } from "./pages/Wrap/Wrap";
import { AppWrapper } from "./wrappers/AppWrapper/AppWrapper";
import { NavbarWrapper } from "components/NavbarV2/NavbarWrapper";
import { BEBOP_ROUTES } from "./config/consts";
import Home from "pages/Home/Home";
import { QueryClient, QueryClientProvider } from "react-query";
import { MODAL_SELECTORS } from "./components/Modal/instance/modalSelectors";
import Approvals from "./components/Modal/instance/Allowance/Approvals";
import { ExtraErrorData, CaptureConsole } from "@sentry/integrations";

Sentry.init({
  dsn: "https://50ab6fc7457749eeb718b5a6b20a7710@sentry.bebop.xyz/2",
  environment: getEnvString(),
  release: APP_VERSION,
  enabled: !import.meta.env.DEV,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
    // Serialize objects going to sentry down to 5 levels.
    new ExtraErrorData({ depth: 5 }),
    new CaptureConsole(),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.5,
  replaysOnErrorSampleRate: 1.0,
});
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);
const queryClient = new QueryClient();

const App: React.FC = () => {
  const theme = GlobalStore.useState(s => s.theme);
  const modalSelector = GlobalStore.useState(s => s.modalSelector);

  return (
    <Sentry.ErrorBoundary fallback={<div> An error has occurred </div>} showDialog>
      <Web3>
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <AppWrapper>
                <NavbarWrapper>
                  <SentryRoutes>
                    <Route path="/" element={<Home />} />
                    <Route path={BEBOP_ROUTES.Trade} element={<Trade />} />
                    <Route path={BEBOP_ROUTES.Activity} element={<Activity />} />
                    <Route path={BEBOP_ROUTES.Wrap} element={<Wrap />} />
                    <Route path={BEBOP_ROUTES.PageNotFound} element={<NotFound />} />
                    {/*<Route path="/maintenance" element={MaintenancePage}/>*/}
                  </SentryRoutes>
                </NavbarWrapper>
                {/* We need the approvals modal to be here, so it doesn't get re-rendered by NavbarWrapper and RainbowKit on window resize */}
                {modalSelector === MODAL_SELECTORS.APPROVE_ALLOWANCE && (
                  <Approvals modalSelector={modalSelector} />
                )}
              </AppWrapper>
            </QueryClientProvider>
          </ThemeProvider>
        </BrowserRouter>
      </Web3>
    </Sentry.ErrorBoundary>
  );
};

export default Sentry.withProfiler(App);
